/**
 * Prospection — activity per user.
 *
 * Who did what on the prospection board over a period: one line per user
 * (calls and what they gave, emails, meetings, notes, prospects touched,
 * stage moves, a contacts-per-day sparkline, and the portfolio they own
 * today — open and late), the team's totals on top, and underneath the
 * journal across prospects. Clicking a user narrows the journal to them;
 * clicking a journal line opens the prospect.
 *
 * Two calls: GET /v2/prospection/activity-stats for the table, and
 * GET /v2/prospection/activity-feed for the journal (paged).
 */
define('prospectionActivityView',[
  'require',
  'module',
  'app',
  'marionette',
  'underscore',
  'jquery',
  'entities/prospection'
], function (require, Module, App, Marionette, _, $) {
  'use strict';

  var FEED_PAGE = 50;
  var STORAGE_KEY = 'prospection.activityPeriod';

  var PERIODS = [
    { key: 'today', label: _.i18n('prospection.period.today', null, 'Today') },
    { key: 'week', label: _.i18n('prospection.period.week', null, 'This week') },
    { key: 'lastWeek', label: _.i18n('prospection.period.lastWeek', null, 'Last week') },
    { key: 'month', label: _.i18n('prospection.period.month', null, 'This month') },
    { key: '30d', label: _.i18n('prospection.period.30d', null, 'Last 30 days') },
    { key: 'custom', label: _.i18n('prospection.period.custom', null, 'Custom') }
  ];

  var EVENT_META = {
    CALL: { icon: 'fa-phone', cls: 'call', label: _.i18n('prospection.log.call', null, 'Call') },
    EMAIL: { icon: 'fa-envelope-o', cls: 'mail', label: _.i18n('prospection.log.email', null, 'Email') },
    MEETING: { icon: 'fa-calendar', cls: 'meet', label: _.i18n('prospection.log.meeting', null, 'Meeting') },
    NOTE: { icon: 'fa-sticky-note-o', cls: 'note', label: _.i18n('prospection.log.note', null, 'Note') },
    STAGE: { icon: 'fa-flag', cls: 'sys', label: _.i18n('prospection.journal.stage', null, 'Stage') },
    ASSIGN: { icon: 'fa-user', cls: 'sys', label: _.i18n('prospection.journal.assign', null, 'Owner') },
    NEXT_ACTION: { icon: 'fa-clock-o', cls: 'sys', label: _.i18n('prospection.journal.next', null, 'Next action') },
    ENROLLED: { icon: 'fa-plus-circle', cls: 'sys', label: _.i18n('prospection.journal.enrolled', null, 'Added to the board') }
  };

  var OUTCOME_LABEL = {
    REACHED: _.i18n('prospection.phone.REACHED', null, 'Reached'),
    CALLBACK: _.i18n('prospection.phone.CALLBACK', null, 'Call back'),
    NO_ANSWER: _.i18n('prospection.phone.NO_ANSWER', null, 'No answer'),
    VOICEMAIL: _.i18n('prospection.phone.VOICEMAIL', null, 'Voicemail'),
    REFUSED: _.i18n('prospection.phone.REFUSED', null, 'Refused'),
    WRONG_NUMBER: _.i18n('prospection.phone.WRONG_NUMBER', null, 'Wrong number'),
    SENT: _.i18n('prospection.email.SENT', null, 'Sent'),
    FOLLOW_UP: _.i18n('prospection.email.FOLLOW_UP', null, 'Follow-up sent'),
    REPLIED: _.i18n('prospection.email.REPLIED', null, 'Replied'),
    BOUNCED: _.i18n('prospection.email.BOUNCED', null, 'Bounced'),
    NEW: _.i18n('prospection.stage.NEW', null, 'New'),
    CONTACTED: _.i18n('prospection.stage.CONTACTED', null, 'Contacted'),
    INTERESTED: _.i18n('prospection.stage.INTERESTED', null, 'Interested'),
    QUOTE_SENT: _.i18n('prospection.stage.QUOTE_SENT', null, 'Quote sent'),
    ON_HOLD: _.i18n('prospection.stage.ON_HOLD', null, 'On hold'),
    WON: _.i18n('prospection.stage.WON', null, 'Won'),
    LOST: _.i18n('prospection.stage.LOST', null, 'Lost')
  };

  function userDisplayName(u) {
    if (!u) { return ''; }
    var full = $.trim(((u.firstName || '') + ' ' + (u.lastName || '')).replace(/\s+/g, ' '));
    return full || u.email || u.logonId || u.secId || '';
  }

  function initials(name) {
    if (!name) { return '?'; }
    var parts = $.trim(name).split(/\s+/);
    if (parts.length === 1) { return parts[0].slice(0, 2).toUpperCase(); }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  function avatarColor(name) {
    var str = String(name || '');
    var hash = 0;
    for (var i = 0; i < str.length; i++) {
      hash = (hash * 31 + str.charCodeAt(i)) % 997;
    }
    return 'c' + (hash % 10);
  }

  function formatDate(value) {
    if (!value) { return ''; }
    var d = new Date(value);
    if (isNaN(d.getTime())) { return ''; }
    return d.toLocaleString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  function formatDay(value) {
    if (!value) { return ''; }
    var d = new Date(value);
    if (isNaN(d.getTime())) { return ''; }
    return d.toLocaleDateString('fr-BE', { weekday: 'short', day: '2-digit', month: '2-digit' });
  }

  function toInputDate(d) {
    function two(n) { return (n < 10 ? '0' : '') + n; }
    return d.getFullYear() + '-' + two(d.getMonth() + 1) + '-' + two(d.getDate());
  }

  function dayStart(d) {
    var c = new Date(d.getTime());
    c.setHours(0, 0, 0, 0);
    return c;
  }

  function addDays(d, n) {
    var c = new Date(d.getTime());
    c.setDate(c.getDate() + n);
    return c;
  }

  // Monday of the week `d` falls in.
  function weekStart(d) {
    var c = dayStart(d);
    var back = (c.getDay() + 6) % 7;
    return addDays(c, -back);
  }

  // {from, to} (both inclusive days) of a named period.
  function periodRange(key, custom) {
    var today = dayStart(new Date());
    switch (key) {
      case 'today':
        return { from: today, to: today };
      case 'lastWeek':
        return { from: addDays(weekStart(today), -7), to: addDays(weekStart(today), -1) };
      case 'month':
        return { from: new Date(today.getFullYear(), today.getMonth(), 1), to: today };
      case '30d':
        return { from: addDays(today, -29), to: today };
      case 'custom':
        if (custom && custom.from && custom.to) {
          return { from: dayStart(new Date(custom.from)), to: dayStart(new Date(custom.to)) };
        }
        return { from: weekStart(today), to: today };
      default:
        return { from: weekStart(today), to: today };
    }
  }

  function pct(part, whole) {
    if (!whole) { return 0; }
    return Math.round(part / whole * 100);
  }

  // A row of tiny bars, one per day; the tallest day of the line fills the height.
  function sparkline(daily, days) {
    var max = _.max(daily.concat([1]));
    return '<span class="pa-spark">' + _.map(daily, function (n, i) {
      var h = n ? Math.max(12, Math.round(n / max * 100)) : 4;
      return '<i class="' + (n ? '' : 'zero') + '" style="height:' + h + '%" title="' + _.escape(formatDay(days[i])) + ' — ' + n + '"></i>';
    }).join('') + '</span>';
  }

  Module.exports = Marionette.ItemView.extend({
    className: 'prospection-activity-view',

    template: _.template(
      '<div class="pa-bar">' +
      '  <button type="button" class="pa-back js-board"><i class="fa fa-th-list"></i> <%= boardLabel %></button>' +
      '  <span class="pa-title"><%= title %></span>' +
      '  <span class="pa-sep"></span>' +
      '  <% _.each(periods, function (p) { %>' +
      '  <button type="button" class="pa-tab js-period <%= p.key === period ? "on" : "" %>" data-period="<%= p.key %>"><%= p.label %></button>' +
      '  <% }); %>' +
      '  <span class="pa-custom js-custom" <%= period === "custom" ? "" : "style=display:none" %>>' +
      '    <input type="date" class="js-from" value="<%= from %>"/> → <input type="date" class="js-to" value="<%= to %>"/>' +
      '  </span>' +
      '  <span class="pa-grow"></span>' +
      '  <span class="pa-range js-range"></span>' +
      '  <button type="button" class="pa-reload js-refresh" title="<%= refreshLabel %>">⟲</button>' +
      '</div>' +
      '<div class="pa-scroll">' +
      '  <div class="pa-tiles js-tiles"></div>' +
      '  <div class="pa-card">' +
      '    <table class="pa-table">' +
      '      <thead><tr>' +
      '        <th class="u"><%= hUser %></th>' +
      '        <th class="n" title="<%= hContactsTitle %>"><%= hContacts %></th>' +
      '        <th class="s"><%= hDaily %></th>' +
      '        <th class="n"><%= hTouched %></th>' +
      '        <th class="c"><%= hCalls %></th>' +
      '        <th class="c"><%= hEmails %></th>' +
      '        <th class="n"><%= hMeetings %></th>' +
      '        <th class="n"><%= hNotes %></th>' +
      '        <th class="m"><%= hMoves %></th>' +
      '        <th class="p" title="<%= hPortfolioTitle %>"><%= hPortfolio %></th>' +
      '      </tr></thead>' +
      '      <tbody class="js-rows"><tr><td colspan="10" class="pa-state"><%= loading %></td></tr></tbody>' +
      '    </table>' +
      '  </div>' +
      '  <div class="pa-feed-head">' +
      '    <h6><%= feedTitle %> <span class="js-feed-who"></span></h6>' +
      '    <label class="pa-toggle"><input type="checkbox" class="js-contacts-only" checked/><%= contactsOnlyLabel %></label>' +
      '    <button type="button" class="pa-clear js-clear-user" style="display:none"><%= everybodyLabel %></button>' +
      '  </div>' +
      '  <ul class="pa-feed js-feed"></ul>' +
      '  <div class="pa-footer js-footer"></div>' +
      '</div>'
    ),

    ui: {
      tiles: '.js-tiles',
      rows: '.js-rows',
      feed: '.js-feed',
      footer: '.js-footer',
      range: '.js-range',
      custom: '.js-custom',
      from: '.js-from',
      to: '.js-to',
      feedWho: '.js-feed-who',
      clearUser: '.js-clear-user',
      contactsOnly: '.js-contacts-only'
    },

    events: {
      'click .js-board': 'onBoard',
      'click .js-period': 'onPeriod',
      'change .js-from': 'onCustomChange',
      'change .js-to': 'onCustomChange',
      'click .js-refresh': 'fetchAll',
      'click .pa-user-row': 'onUserClick',
      'click .js-clear-user': 'onClearUser',
      'change .js-contacts-only': 'onContactsOnly',
      'click .pa-ev': 'onEventClick',
      'click .js-more': 'onMore'
    },

    initialize: function () {
      var stored = {};
      try {
        stored = JSON.parse((window.localStorage && window.localStorage.getItem(STORAGE_KEY)) || '{}');
      } catch (e) { stored = {}; }
      this.period = stored.period || 'week';
      this.custom = stored.custom || null;
      this.userSecId = null;
      this.userName = '';
      this.feedItems = [];
      this.feedTotal = 0;
    },

    serializeData: function () {
      var range = periodRange(this.period, this.custom);
      return {
        title: _.i18n('prospection.activity.title', null, 'Prospecting activity'),
        boardLabel: _.i18n('prospection.activity.board', null, 'Board'),
        periods: PERIODS,
        period: this.period,
        from: toInputDate(range.from),
        to: toInputDate(range.to),
        refreshLabel: _.i18n('common.refresh', null, 'Refresh'),
        loading: _.i18n('common.loading', null, 'Loading…'),
        hUser: _.i18n('prospection.activity.user', null, 'User'),
        hContacts: _.i18n('prospection.activity.contacts', null, 'Contacts'),
        hContactsTitle: _.i18n('prospection.activity.contacts.title', null, 'Calls + emails + meetings'),
        hDaily: _.i18n('prospection.activity.daily', null, 'Per day'),
        hTouched: _.i18n('prospection.activity.touched', null, 'Prospects'),
        hCalls: _.i18n('prospection.activity.calls', null, 'Calls'),
        hEmails: _.i18n('prospection.activity.emails', null, 'Emails'),
        hMeetings: _.i18n('prospection.activity.meetings', null, 'Meetings'),
        hNotes: _.i18n('prospection.activity.notes', null, 'Notes'),
        hMoves: _.i18n('prospection.activity.moves', null, 'Pipeline moves'),
        hPortfolio: _.i18n('prospection.activity.portfolio', null, 'Portfolio'),
        hPortfolioTitle: _.i18n('prospection.activity.portfolio.title', null, 'Open prospects owned today, and how many have a late next action'),
        feedTitle: _.i18n('prospection.activity.feed', null, 'Journal'),
        contactsOnlyLabel: _.i18n('prospection.activity.contactsOnly', null, 'Calls, emails and meetings only'),
        everybodyLabel: _.i18n('prospection.activity.everybody', null, 'Show everybody')
      };
    },

    onShow: function () {
      this.fetchAll();
    },

    range: function () {
      return periodRange(this.period, this.custom);
    },

    savePeriod: function () {
      try {
        if (window.localStorage) {
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ period: this.period, custom: this.custom }));
        }
      } catch (e) { /* ignore */ }
    },

    // ---- data ---------------------------------------------------------------
    fetchAll: function () {
      this.fetchStats();
      this.fetchFeed();
    },

    fetchStats: function () {
      var self = this;
      var r = this.range();
      this.ui.range.text(formatDay(r.from) + (r.from.getTime() === r.to.getTime() ? '' : ' → ' + formatDay(r.to)));
      App.request('prospection:activity-stats', { from: r.from.getTime(), to: r.to.getTime() }).done(function (stats) {
        if (self.isDestroyed) { return; }
        self.stats = stats || { users: [], team: {}, days: [] };
        self.renderTiles();
        self.renderRows();
      }).fail(function (xhr) {
        if (self.isDestroyed) { return; }
        var forbidden = xhr && xhr.status === 403;
        self.ui.tiles.empty();
        self.ui.rows.html('<tr><td colspan="10" class="pa-state bad">' + (forbidden ?
          _.i18n('prospection.activity.managersOnly', null, 'This screen is reserved for managers.') :
          _.i18n('prospection.activity.loadError', null, 'Could not load the activity.')) + '</td></tr>');
        if (forbidden) {
          self.ui.feed.empty();
          self.ui.footer.empty();
          self.$('.pa-feed-head').hide();
        }
      });
    },

    fetchFeed: function (append) {
      var self = this;
      var r = this.range();
      if (!append) {
        this.feedItems = [];
        this.ui.feed.html('<li class="pa-state">' + _.i18n('common.loading', null, 'Loading…') + '</li>');
      }
      var params = { from: r.from.getTime(), to: r.to.getTime(), first: this.feedItems.length, pageSize: FEED_PAGE };
      if (this.userSecId) { params.userSecId = this.userSecId; }
      if (this.ui.contactsOnly.is(':checked')) { params.contactsOnly = true; }
      App.request('prospection:activity-feed', params).done(function (page) {
        if (self.isDestroyed) { return; }
        self.feedItems = self.feedItems.concat((page && page.items) || []);
        self.feedTotal = (page && page.total) || 0;
        self.renderFeed();
      }).fail(function () {
        if (self.isDestroyed) { return; }
        self.ui.feed.empty();
        self.ui.footer.empty();
      });
    },

    // ---- rendering ----------------------------------------------------------
    renderTiles: function () {
      var t = this.stats.team || {};
      var active = _.filter(this.stats.users || [], function (u) { return u.contacts > 0; }).length;
      var tiles = [
        { n: t.contacts || 0, l: _.i18n('prospection.activity.contacts', null, 'Contacts'), s: active + ' ' + _.i18n('prospection.activity.activeUsers', null, 'active users') },
        { n: t.prospectsTouched || 0, l: _.i18n('prospection.activity.touchedLong', null, 'Prospects touched'), s: '' },
        { n: t.calls || 0, l: _.i18n('prospection.activity.calls', null, 'Calls'), s: pct((t.callsReached || 0) + (t.callsCallback || 0), t.calls) + '% ' + _.i18n('prospection.activity.reached', null, 'reached') },
        { n: t.emails || 0, l: _.i18n('prospection.activity.emails', null, 'Emails'), s: (t.emailsReplied || 0) + ' ' + _.i18n('prospection.activity.replies', null, 'replies') },
        { n: t.meetings || 0, l: _.i18n('prospection.activity.meetings', null, 'Meetings'), s: '' },
        { n: (t.toInterested || 0) + (t.toQuoteSent || 0), l: _.i18n('prospection.activity.progress', null, 'Interested / quotes'), s: (t.toInterested || 0) + ' + ' + (t.toQuoteSent || 0) },
        { n: t.toWon || 0, l: _.i18n('prospection.stage.WON', null, 'Won'), s: (t.toLost || 0) + ' ' + _.i18n('prospection.activity.lost', null, 'lost'), tone: t.toWon ? 'good' : '' },
        { n: t.ownedOverdue || 0, l: _.i18n('prospection.activity.late', null, 'Late actions'), s: (t.ownedOpen || 0) + ' ' + _.i18n('prospection.kpi.open', null, 'open'), tone: t.ownedOverdue ? 'bad' : '' }
      ];
      this.ui.tiles.html(_.map(tiles, function (x) {
        return '<div class="pa-tile ' + (x.tone || '') + '"><b>' + x.n + '</b><span>' + _.escape(x.l) + '</span><small>' + _.escape(x.s) + '</small></div>';
      }).join(''));
    },

    rowHtml: function (u, days, isTeam) {
      var name = isTeam ? _.i18n('prospection.activity.team', null, 'Team') : userDisplayName(u.user);
      var reached = (u.callsReached || 0) + (u.callsCallback || 0);
      var missed = (u.callsNoAnswer || 0) + (u.callsVoicemail || 0);
      var dead = (u.callsRefused || 0) + (u.callsWrongNumber || 0);
      var callsTitle = [
        OUTCOME_LABEL.REACHED + ': ' + (u.callsReached || 0),
        OUTCOME_LABEL.CALLBACK + ': ' + (u.callsCallback || 0),
        OUTCOME_LABEL.NO_ANSWER + ': ' + (u.callsNoAnswer || 0),
        OUTCOME_LABEL.VOICEMAIL + ': ' + (u.callsVoicemail || 0),
        OUTCOME_LABEL.REFUSED + ': ' + (u.callsRefused || 0),
        OUTCOME_LABEL.WRONG_NUMBER + ': ' + (u.callsWrongNumber || 0)
      ].join('\n');
      var emailsTitle = [
        OUTCOME_LABEL.SENT + ': ' + (u.emailsSent || 0),
        OUTCOME_LABEL.FOLLOW_UP + ': ' + (u.emailsFollowUp || 0),
        OUTCOME_LABEL.REPLIED + ': ' + (u.emailsReplied || 0),
        OUTCOME_LABEL.BOUNCED + ': ' + (u.emailsBounced || 0)
      ].join('\n');
      var callBar = u.calls ?
        '<span class="pa-split" title="' + _.escape(callsTitle) + '">' +
        '<i class="ok" style="width:' + pct(reached, u.calls) + '%"></i>' +
        '<i class="try" style="width:' + pct(missed, u.calls) + '%"></i>' +
        '<i class="bad" style="width:' + pct(dead, u.calls) + '%"></i></span>' : '';
      var moves = _.compact([
        u.toInterested ? '<span class="pa-move interested" title="' + OUTCOME_LABEL.INTERESTED + '">' + u.toInterested + '</span>' : '',
        u.toQuoteSent ? '<span class="pa-move quote" title="' + OUTCOME_LABEL.QUOTE_SENT + '">' + u.toQuoteSent + '</span>' : '',
        u.toWon ? '<span class="pa-move won" title="' + OUTCOME_LABEL.WON + '">' + u.toWon + '</span>' : '',
        u.toLost ? '<span class="pa-move lost" title="' + OUTCOME_LABEL.LOST + '">' + u.toLost + '</span>' : ''
      ]).join('');
      var secId = !isTeam && u.user ? u.user.secId : '';
      return '<tr class="' + (isTeam ? 'pa-team-row' : 'pa-user-row' + (secId && secId === this.userSecId ? ' on' : '')) + '"' +
        ' data-secid="' + _.escape(secId) + '" data-name="' + _.escape(name) + '">' +
        '<td class="u">' + (isTeam ? '<span class="pa-uav team"><i class="fa fa-users"></i></span>' :
          '<span class="pa-uav ' + avatarColor(name) + '">' + _.escape(initials(name)) + '</span>') +
        '<span class="pa-uname">' + _.escape(name) + '</span></td>' +
        '<td class="n big">' + (u.contacts || 0) + '</td>' +
        '<td class="s">' + sparkline(u.daily || [], days) + '</td>' +
        '<td class="n">' + (u.prospectsTouched || 0) + '</td>' +
        '<td class="c"><b>' + (u.calls || 0) + '</b>' + (u.calls ? '<small>' + reached + ' ' + _.i18n('prospection.activity.reached', null, 'reached') + '</small>' : '') + callBar + '</td>' +
        '<td class="c" title="' + _.escape(emailsTitle) + '"><b>' + (u.emails || 0) + '</b>' +
          (u.emailsReplied ? '<small>' + u.emailsReplied + ' ' + _.i18n('prospection.activity.replies', null, 'replies') + '</small>' : '') + '</td>' +
        '<td class="n">' + (u.meetings || 0) + '</td>' +
        '<td class="n">' + (u.notes || 0) + '</td>' +
        '<td class="m">' + (moves || '<span class="pa-none">—</span>') + '</td>' +
        '<td class="p"><b>' + (u.ownedOpen || 0) + '</b>' +
          (u.ownedOverdue ? '<span class="pa-late">' + u.ownedOverdue + ' ' + _.i18n('prospection.due.late', null, 'late') + '</span>' : '') + '</td>' +
        '</tr>';
    },

    renderRows: function () {
      var users = this.stats.users || [];
      var days = this.stats.days || [];
      if (!users.length) {
        this.ui.rows.html('<tr><td colspan="10" class="pa-state">' +
          _.i18n('prospection.activity.empty', null, 'Nobody logged anything over this period.') + '</td></tr>');
        return;
      }
      var html = _.map(users, function (u) { return this.rowHtml(u, days, false); }, this).join('');
      html += this.rowHtml(this.stats.team || {}, days, true);
      this.ui.rows.html(html);
    },

    renderFeed: function () {
      if (!this.feedItems.length) {
        this.ui.feed.html('<li class="pa-state">' + _.i18n('prospection.activity.feedEmpty', null, 'Nothing in the journal for this period.') + '</li>');
        this.ui.footer.empty();
        return;
      }
      this.ui.feed.html(_.map(this.feedItems, function (ev) {
        var meta = EVENT_META[ev.type] || { icon: 'fa-circle-o', cls: 'sys', label: ev.type };
        var who = userDisplayName(ev.user) || _.i18n('prospection.journal.system', null, 'System');
        var prospect = ev.customerName || ev.companyName || ev.customerEmail || '';
        var outcome = OUTCOME_LABEL[ev.outcome] || ev.outcome || '';
        return '<li class="pa-ev ' + meta.cls + '" data-secid="' + _.escape(ev.prospectionSecId || '') + '" data-name="' + _.escape(prospect) + '">' +
          '<span class="pa-ev-ic"><i class="fa ' + meta.icon + '"></i></span>' +
          '<span class="pa-ev-main">' +
          '<span class="pa-ev-t"><b>' + _.escape(who) + '</b> · ' + _.escape(meta.label) +
          (outcome ? '<span class="pa-ev-out">' + _.escape(outcome) + '</span>' : '') +
          '<span class="pa-ev-p">' + _.escape(prospect) + '</span></span>' +
          (ev.note ? '<span class="pa-ev-note">' + _.escape(ev.note) + '</span>' : '') +
          '</span>' +
          '<span class="pa-ev-d">' + _.escape(formatDate(ev.activityAt)) + '</span></li>';
      }).join(''));
      var shown = this.feedItems.length;
      this.ui.footer.html('<span><b>' + shown + '</b> ' + _.i18n('common.of', null, 'of') + ' <b>' + this.feedTotal + '</b></span>' +
        (shown < this.feedTotal ? '<button type="button" class="pa-more js-more">' + _.i18n('common.showMore', null, 'Show more') + '</button>' : ''));
    },

    // ---- interactions -------------------------------------------------------
    onBoard: function () {
      App.navigate('prospection', { trigger: true });
    },

    onPeriod: function (e) {
      this.period = $(e.currentTarget).attr('data-period');
      this.$('.js-period').removeClass('on');
      $(e.currentTarget).addClass('on');
      this.ui.custom.toggle(this.period === 'custom');
      if (this.period === 'custom' && !this.custom) {
        this.custom = { from: this.ui.from.val(), to: this.ui.to.val() };
      }
      this.savePeriod();
      this.fetchAll();
    },

    onCustomChange: function () {
      var from = this.ui.from.val();
      var to = this.ui.to.val();
      if (!from || !to) { return; }
      if (from > to) { var swap = from; from = to; to = swap; }
      this.custom = { from: from, to: to };
      this.savePeriod();
      this.fetchAll();
    },

    // A user's line narrows the journal to them; a second click widens it back.
    onUserClick: function (e) {
      var $row = $(e.currentTarget);
      var secId = $row.attr('data-secid');
      if (!secId) { return; }
      if (this.userSecId === secId) {
        this.onClearUser();
        return;
      }
      this.userSecId = secId;
      this.userName = $row.attr('data-name');
      this.$('.pa-user-row').removeClass('on');
      $row.addClass('on');
      this.ui.feedWho.text('— ' + this.userName);
      this.ui.clearUser.show();
      this.fetchFeed();
    },

    onClearUser: function () {
      this.userSecId = null;
      this.userName = '';
      this.$('.pa-user-row').removeClass('on');
      this.ui.feedWho.text('');
      this.ui.clearUser.hide();
      this.fetchFeed();
    },

    onContactsOnly: function () {
      this.fetchFeed();
    },

    onEventClick: function (e) {
      var $li = $(e.currentTarget);
      var secId = $li.attr('data-secid');
      if (!secId) { return; }
      var name = $li.attr('data-name');
      require(['prospectionWindowView'], function (WindowView) {
        WindowView.prototype.open(secId, name ? { secId: secId, customerName: name } : null);
      });
    },

    onMore: function () {
      this.fetchFeed(true);
    }
  });
});

